import React, { useMemo, useState } from 'react';
import { ModuleHeader } from '../components/ui/RdaUI';

export default function SurgeonHub() {
  const [batchJson, setBatchJson] = useState('');
  const [parsedBatch, setParsedBatch] = useState(null);
  const [parsedBatches, setParsedBatches] = useState(null);
  const [parseError, setParseError] = useState('');

  const [isDryRunning, setIsDryRunning] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [isMultiRunning, setIsMultiRunning] = useState(false);
  const [multiStatus, setMultiStatus] = useState('');
  const [multiProgressPct, setMultiProgressPct] = useState(0);

  const [diffText, setDiffText] = useState('');
  const [syncContextText, setSyncContextText] = useState('');
  const [reportText, setReportText] = useState('');
  const [errorText, setErrorText] = useState('');

  const canRun = Boolean(
    (parsedBatch || (parsedBatches && parsedBatches.length)) &&
    !parseError &&
    !isDryRunning &&
    !isExecuting &&
    !isMultiRunning
  );

  const surgeonBaseUrl = useMemo(() => {
    const envUrl = String(import.meta?.env?.VITE_SURGEON_URL || import.meta?.env?.VITE_SURGEON_BASE_URL || '').trim();
    const host = typeof window !== 'undefined' ? String(window.location.hostname || '127.0.0.1') : '127.0.0.1';
    const raw = (envUrl || `http://${host}:8010`).trim();

    let normalized = raw;
    if (normalized.startsWith(':')) {
      normalized = `http://${host}${normalized}`;
    } else if (!/^https?:\/\//i.test(normalized)) {
      normalized = `http://${normalized}`;
    }

    return normalized.replace(/\/+$/, '');
  }, []);

  const validateAndParse = () => {
    const raw = String(batchJson || '').trim();
    if (!raw) {
      setParseError('Cole um Array JSON de cirurgias.');
      setParsedBatch(null);
      setParsedBatches(null);
      return null;
    }

    try {
      const obj = JSON.parse(raw);
      const normalizeBatchArray = (value) => {
        if (!Array.isArray(value)) return null;
        if (!value.length) return [];
        return value;
      };

      const isBatchItemArray = (arr) => Array.isArray(arr) && arr.every((x) => x && typeof x === 'object' && !Array.isArray(x));

      const isArrayOfBatches = (arr) => Array.isArray(arr) && arr.every((b) => Array.isArray(b));

      if (Array.isArray(obj)) {
        if (obj.length === 0) {
          setParseError('O JSON não pode ser vazio.');
          setParsedBatch(null);
          setParsedBatches(null);
          return null;
        }

        if (isBatchItemArray(obj)) {
          setParseError('');
          setParsedBatch(obj);
          setParsedBatches(null);
          return { mode: 'single', batch: obj };
        }

        if (isArrayOfBatches(obj)) {
          const batches = obj.map((b) => normalizeBatchArray(b) || []);
          if (!batches.length || batches.some((b) => !Array.isArray(b) || b.length === 0)) {
            setParseError('Cada lote deve ser um Array JSON não vazio de cirurgias.');
            setParsedBatch(null);
            setParsedBatches(null);
            return null;
          }
          setParseError('');
          setParsedBatch(null);
          setParsedBatches(batches);
          return { mode: 'multi', batches };
        }

        setParseError('Formato inválido. Use: Array de cirurgias (lote único) OU Array de Arrays (multi-lotes).');
        setParsedBatch(null);
        setParsedBatches(null);
        return null;
      }

      if (obj && typeof obj === 'object' && Array.isArray(obj.batches)) {
        const batches = obj.batches.map((b) => normalizeBatchArray(b) || []);
        if (!batches.length || batches.some((b) => !Array.isArray(b) || b.length === 0)) {
          setParseError('Em {"batches": [...]}, cada lote deve ser um Array JSON não vazio de cirurgias.');
          setParsedBatch(null);
          setParsedBatches(null);
          return null;
        }
        setParseError('');
        setParsedBatch(null);
        setParsedBatches(batches);
        return { mode: 'multi', batches };
      }

      setParseError('O JSON deve ser um Array (lote único) ou um Array de Arrays / {"batches": [...]} (multi-lotes).');
      setParsedBatch(null);
      setParsedBatches(null);
      return null;
    } catch (e) {
      setParseError(String(e?.message || e || 'JSON inválido'));
      setParsedBatch(null);
      setParsedBatches(null);
      return null;
    }
  };

  const payloadPreview = useMemo(() => {
    if (parsedBatch) return parsedBatch;
    if (parsedBatches && parsedBatches.length) return parsedBatches;
    return null;
  }, [parsedBatch, parsedBatches]);

  const _runRequest = async ({ payload, dryRun }) => {
    const res = await fetch(`${surgeonBaseUrl}/api/v1/surgery/inject?dry_run=${dryRun ? 'true' : 'false'}` , {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const dataText = await res.text();
    let data;
    try {
      data = JSON.parse(dataText);
    } catch (_e) {
      data = null;
    }

    if (!res.ok) {
      return {
        ok: false,
        status: res.status,
        errorText: String((data && (data.error || dataText)) || dataText || `Erro HTTP ${res.status}`),
        data,
      };
    }

    const diff = (data && (data.diff || data.diff_text)) || '';
    const report = (data && (data.report_markdown || data.report)) || '';
    const sync = (data && data.sync_context) || null;

    return {
      ok: true,
      status: res.status,
      diff: String(diff || ''),
      report: String(report || ''),
      sync,
      data,
    };
  };

  const runInject = async (dryRun) => {
    if (!canRun) return;
    const parsed = validateAndParse();
    if (!parsed) return;
    if (parsed.mode && parsed.mode !== 'single') {
      setParseError('Modo multi-lotes detectado. Use o botão de execução Multi-Lote.');
      return;
    }

    const payload = parsed.batch || parsed;
    if (!payload || !Array.isArray(payload)) return;

    setErrorText('');
    setReportText('');
    setDiffText('');
    setSyncContextText('');

    if (dryRun) setIsDryRunning(true);
    else setIsExecuting(true);

    try {
      const r = await _runRequest({ payload, dryRun });
      if (!r.ok) {
        setErrorText(String(r.errorText || 'Falha na requisição.'));
        return;
      }

      setDiffText(String(r.diff || ''));
      setReportText(String(r.report || ''));

      const sync = r.sync;
      if (sync && typeof sync === 'object') {
        const block = Object.entries(sync)
          .map(([fp, content]) => `# FILE: ${fp}\n\n${String(content || '')}\n`)
          .join('\n\n');
        setSyncContextText(block.trim());
      } else {
        setSyncContextText('');
      }
    } catch (e) {
      setErrorText(String(e?.message || e || 'Erro desconhecido'));
    } finally {
      if (dryRun) setIsDryRunning(false);
      else setIsExecuting(false);
    }
  };

  const runMulti = async () => {
    if (!canRun) return;
    const parsed = validateAndParse();
    if (!parsed) return;

    const batches = parsed.batches || parsedBatches;
    if (!batches || !Array.isArray(batches) || !batches.length) {
      setParseError('Nenhum multi-lote válido detectado.');
      return;
    }

    setErrorText('');
    setReportText('');
    setDiffText('');
    setSyncContextText('');

    setIsMultiRunning(true);
    setMultiStatus('Iniciando...');
    setMultiProgressPct(0);

    const reportParts = [];
    const diffParts = [];

    try {
      for (let i = 0; i < batches.length; i += 1) {
        const payload = batches[i];
        const loteLabel = `LOTE ${i + 1}/${batches.length}`;

        const totalSteps = Math.max(1, batches.length * 2);
        const baseStep = i * 2;
        setMultiProgressPct(Math.round((baseStep / totalSteps) * 100));

        setMultiStatus(`${loteLabel}: Dry Run...`);
        const dry = await _runRequest({ payload, dryRun: true });
        reportParts.push(`\n\n# ${loteLabel} — DRY RUN\n\n` + String(dry.ok ? (dry.report || '') : (dry.errorText || 'ERRO')));
        diffParts.push(`\n\n# ${loteLabel} — DIFF (DRY RUN)\n\n` + String(dry.ok ? (dry.diff || '') : (dry.errorText || 'ERRO')));

        if (!dry.ok) {
          setErrorText(String(dry.errorText || 'Falha no Dry Run.'));
          setReportText(reportParts.join('\n'));
          setDiffText(diffParts.join('\n'));
          return;
        }

        setMultiProgressPct(Math.round(((baseStep + 1) / totalSteps) * 100));

        setMultiStatus(`${loteLabel}: Executando...`);
        const exec = await _runRequest({ payload, dryRun: false });
        reportParts.push(`\n\n# ${loteLabel} — EXECUÇÃO\n\n` + String(exec.ok ? (exec.report || '') : (exec.errorText || 'ERRO')));
        diffParts.push(`\n\n# ${loteLabel} — DIFF (EXECUÇÃO)\n\n` + String(exec.ok ? (exec.diff || '') : (exec.errorText || 'ERRO')));

        if (!exec.ok) {
          setErrorText(String(exec.errorText || 'Falha na execução.'));
          setReportText(reportParts.join('\n'));
          setDiffText(diffParts.join('\n'));
          return;
        }

        if (exec.sync && typeof exec.sync === 'object') {
          const block = Object.entries(exec.sync)
            .map(([fp, content]) => `# FILE: ${fp}\n\n${String(content || '')}\n`)
            .join('\n\n');
          setSyncContextText(block.trim());
        }
      }

      setMultiStatus(`Concluído: ${batches.length} lote(s) executados.`);
      setMultiProgressPct(100);
      setErrorText('');
      setReportText(reportParts.join('\n'));
      setDiffText(diffParts.join('\n'));
    } catch (e) {
      setErrorText(String(e?.message || e || 'Falha desconhecida no Multi-Lote Runner.'));
      setReportText(reportParts.join('\n'));
      setDiffText(diffParts.join('\n'));
    } finally {
      setIsMultiRunning(false);
      setTimeout(() => {
        setMultiStatus('');
        setMultiProgressPct(0);
      }, 2500);
    }
  };

  const copySyncContext = async () => {
    const text = String(syncContextText || '').trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      setErrorText(String(e?.message || e || 'Falha ao copiar para clipboard'));
    }
  };

  const copyReport = async () => {
    const text = String((errorText || reportText) || '').trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      setErrorText(String(e?.message || e || 'Falha ao copiar para clipboard'));
    }
  };

  return (
    <div data-testid="surgeon-hub" className="w-full h-full flex flex-col overflow-hidden relative z-50">
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden flex flex-col min-h-0">
        <ModuleHeader
          title="Cirurgia RDA"
          subtitle="Motor de Lotes (V5.1) · Dry Run antes da gravação"
          actions={
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={!canRun}
                onClick={() => runInject(true)}
                className={
                  'px-4 py-2 rounded-lg text-[10px] font-extrabold uppercase tracking-widest border transition-colors ' +
                  (canRun
                    ? 'bg-white text-[#20243B] border-gray-300 hover:bg-gray-50'
                    : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed')
                }
              >
                {isDryRunning ? 'Simulando...' : 'Simular Cirurgia (Dry Run)'}
              </button>
              <button
                type="button"
                disabled={!canRun}
                onClick={() => runInject(false)}
                className={
                  'px-4 py-2 rounded-lg text-[10px] font-extrabold uppercase tracking-widest border transition-colors ' +
                  (canRun
                    ? 'bg-[#C5A059] text-[#20243B] border-[#C5A059] hover:brightness-110'
                    : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed')
                }
              >
                {isExecuting ? 'Executando...' : 'Confirmar e Executar Lote'}
              </button>
              <button
                type="button"
                disabled={!canRun || !(parsedBatches && parsedBatches.length)}
                onClick={runMulti}
                className={
                  'px-4 py-2 rounded-lg text-[10px] font-extrabold uppercase tracking-widest border transition-colors ' +
                  (canRun && (parsedBatches && parsedBatches.length)
                    ? 'bg-[#20243B] text-white border-[#20243B] hover:opacity-95'
                    : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed')
                }
                title="Executa cada lote (Dry Run + Execução) em sequência e para no primeiro erro."
              >
                {isMultiRunning ? (multiStatus || 'Executando Multi-Lote...') : 'Multi-Lote Runner (1 clique)'}
              </button>
            </div>
          }
        />
        {(isDryRunning || isExecuting || isMultiRunning) ? (
          <div className="px-6 pb-3 flex-shrink-0 border-b border-gray-200">
            <div className="flex items-center justify-between gap-3">
              <div className="text-[11px] font-bold text-slate-700">
                {isMultiRunning ? (multiStatus || 'Executando multi-lotes...') : (isDryRunning ? 'Simulando (Dry Run)...' : 'Executando lote...')}
              </div>
              <div className="text-[11px] font-extrabold text-slate-600">
                {isMultiRunning ? `${Math.max(0, Math.min(100, Number(multiProgressPct) || 0))}%` : ''}
              </div>
            </div>
            <div className="mt-2 h-2 w-full rounded bg-slate-200 overflow-hidden" aria-hidden="true">
              <div
                className="h-2 rounded bg-[#C5A059]"
                style={{ width: isMultiRunning ? `${Math.max(0, Math.min(100, Number(multiProgressPct) || 0))}%` : '35%' }}
              />
            </div>
            <div className="sr-only" aria-live="polite">
              {isMultiRunning ? (multiStatus || 'Executando multi-lotes') : (isDryRunning ? 'Simulando Dry Run' : 'Executando lote')}
            </div>
          </div>
        ) : null}

        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1 min-h-0 overflow-auto">
          <div className="space-y-4 flex flex-col min-h-0">
            <div>
              <label className="block text-[10px] font-extrabold uppercase tracking-widest text-gray-500">Array JSON de Cirurgias (BatchPayload[])</label>
              <textarea
                value={batchJson}
                onChange={(e) => setBatchJson(e.target.value)}
                onBlur={validateAndParse}
                placeholder={
                  '[\n' +
                  '  {\n' +
                  '    "file_path": "src/.../ProcurationEngine.jsx",\n' +
                  '    "target_node_name": "handleResetNuclear",\n' +
                  '    "injected_code": "function handleResetNuclear() {\\n  // ...\\n}"\n' +
                  '  }\n' +
                  ']\n\nOU MULTI-LOTES:\n[\n  [ { ... }, { ... } ],\n  [ { ... } ]\n]\n\nOU:\n{ "batches": [ [ { ... } ], [ { ... } ] ] }'
                }
                rows={12}
                className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-xs font-mono text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#C5A059]/60 resize-y"
              />
              {parsedBatches && parsedBatches.length ? (
                <div className="mt-2 text-[11px] text-emerald-700 font-semibold">
                  Multi-lotes detectados: {parsedBatches.length}. Use o botão "Multi-Lote Runner (1 clique)".
                </div>
              ) : null}
              {parseError ? <div className="mt-2 text-[11px] text-red-600 font-semibold">{parseError}</div> : null}
            </div>

            <div>
              <div className="text-[10px] font-extrabold uppercase tracking-widest text-gray-500">Diff (Dry Run / Execução)</div>
              <div className="mt-2 border border-gray-200 rounded-xl overflow-hidden bg-[#0b1020]">
                <pre className="p-4 text-[11px] text-gray-100 font-mono overflow-auto max-h-[420px] whitespace-pre-wrap">
                  {diffText || 'Aguardando simulação...'}
                </pre>
              </div>
            </div>

            <div className="pt-2">
              <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Payload (Preview)</div>
              <pre className="mt-2 p-3 rounded-lg border border-gray-200 bg-gray-50 text-[11px] font-mono overflow-auto max-h-[180px] whitespace-pre-wrap">
                {payloadPreview ? JSON.stringify(payloadPreview, null, 2) : 'Cole JSON e clique fora do campo para validar.'}
              </pre>
            </div>
          </div>

          <div className="space-y-4 flex flex-col min-h-0">
            <div>
              <div className="flex items-center justify-between gap-3">
                <div className="text-[10px] font-extrabold uppercase tracking-widest text-gray-500">Laudo / Retorno</div>
                <button
                  type="button"
                  onClick={copyReport}
                  disabled={!String((errorText || reportText) || '').trim()}
                  className={
                    'px-3 py-2 rounded-lg text-[10px] font-extrabold uppercase tracking-widest border transition-all active:scale-95 ' +
                    (String((errorText || reportText) || '').trim()
                      ? 'bg-white text-[#20243B] border-gray-300 hover:bg-gray-50 hover:shadow-sm'
                      : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed')
                  }
                >
                  Copiar Laudo
                </button>
              </div>
              <div className="mt-2 border border-gray-200 rounded-xl overflow-hidden bg-[#0b1020]">
                <pre className="p-4 text-[11px] text-gray-100 font-mono overflow-auto max-h-[520px] whitespace-pre-wrap">
                  {errorText ? errorText : reportText || 'Aguardando execução...'}
                </pre>
              </div>
              {errorText ? (
                <div className="mt-2 text-[11px] text-red-600 font-semibold">Falha na requisição.</div>
              ) : null}
            </div>

            <div className="border border-gray-200 rounded-xl bg-white p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="text-[10px] font-extrabold uppercase tracking-widest text-gray-500">Sincronizador de IA (sync_context)</div>
                <button
                  type="button"
                  onClick={copySyncContext}
                  disabled={!String(syncContextText || '').trim()}
                  className={
                    'px-3 py-2 rounded-lg text-[10px] font-extrabold uppercase tracking-widest border transition-colors ' +
                    (String(syncContextText || '').trim()
                      ? 'bg-white text-[#20243B] border-gray-300 hover:bg-gray-50'
                      : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed')
                  }
                >
                  Copiar Contexto Atualizado
                </button>
              </div>
              <textarea
                value={syncContextText}
                readOnly
                rows={12}
                placeholder="Após Dry Run ou Execução, o backend devolve aqui o conteúdo integral dos arquivos afetados."
                className="mt-3 w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-[11px] font-mono text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#C5A059]/60 resize-y"
              />
            </div>

            <div className="border border-gray-200 rounded-xl bg-white p-4">
              <div className="text-[10px] font-extrabold uppercase tracking-widest text-gray-500">Notas Operacionais</div>
              <div className="mt-2 text-[11px] text-gray-600 leading-relaxed">
                Este painel conversa com <span className="font-mono">{surgeonBaseUrl}</span>. O Acervo/Zotero/WhatsApp permanece isolado na <span className="font-mono">8009</span>. Use Dry Run sempre antes do lote real.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
